import type { SessionUser } from '@/types/api'
import { MIAOLIN_CUSTOMER_ONBOARDING_FORM_ID } from './miaolin-form-ids'
import { isAdminEmail } from './permissions'

export type BusinessRole = 'admin' | 'manager' | 'sales' | 'assistant' | 'none'
export type SalesUserRole = 'sales' | 'assistant' | 'manager'
export type SalesUserStatus = 'active' | 'inactive' | 'blocked'
export type SalesUserErpData = {
  syncedAt: string
  source: 'miaolin_sales_users_sync_webhook'
  employeeId: string
  email: string
  name: string
  role: SalesUserRole
  status?: SalesUserStatus
  title: string
  phone: string
  teamId: string
  teamName: string
  teamCode: string
  departmentName: string
  departmentCode: string
  supervisorEmail: string
  supervisorName: string
  location?: string
  directManager?: string
  departmentManager?: string
  vicePresident?: string
  territory: string
  customerSegment: string
}
export type ApplicationStatus =
  | 'draft'
  | 'pending_review'
  | 'changes_requested'
  | 'approved'
  | 'approved_pending_erp_filing'
  | 'customer_created'
  | 'duplicate_pending'
  | 'rejected'
export type CustomerRecordStatus = 'active' | 'archived'
export type CustomerDataStatus = 'updated' | 'manual_review'
export type CustomerFilingStatus = 'not_filed' | 'filed'

export type SalesUser = {
  id: string
  email: string
  name: string
  employeeId: string
  title: string
  phone: string
  teamId: string
  teamName: string
  teamCode: string
  departmentName: string
  departmentCode: string
  role: SalesUserRole
  supervisorEmail: string
  supervisorName: string
  status: SalesUserStatus
  statusSpecified?: boolean
  territory: string
  customerSegment: string
  notes: string
  erpData?: SalesUserErpData
  passwordConfigured?: boolean
  password?: string
  removePassword?: boolean
}

export type SalesUserImportSummary = {
  imported: number
  created: number
  updated?: number
  skippedExisting: number
  statusChangesIgnored?: number
  errors: string[]
}

export type SalesUserImportResult = {
  salesUsers: SalesUser[]
  summary: SalesUserImportSummary
}

export const salesUserImportRoleAliases = [
  'manager',
  '業務主管',
  '主管',
  'sales',
  '業務',
  'assistant',
  'agent',
  '業務助理',
] as const

export const salesUserImportStatusAliases = [
  'active',
  '啟用',
  '在職',
  'inactive',
  'disabled',
  '停用',
  '離職',
  'blocked',
  '封鎖',
] as const

export const salesUserImportEmailHeaderAliases = [
  'email',
  'mail',
  '帳號',
  '電子郵件',
  '業務email',
  '業務電子郵件',
  '業務信箱',
  '業務mail',
] as const

const salesUserImportNameHeaderAliases = [
  'name',
  '姓名',
  'displayName',
  '顯示名稱',
  '業務簡稱',
  '業務姓名',
  '業務名稱',
] as const

const salesUserImportSalesCodeHeaderAliases = [
  'salesCode',
  'employeeId',
  'employee_id',
  '業務代號',
  '員工編號',
  '員工代號',
] as const

const salesUserImportTeamCodeHeaderAliases = [
  'teamCode',
  'team_code',
  'teamNo',
  'team_no',
  '組別代號',
  '組別',
  '團隊代號',
] as const

const salesUserImportSupervisorNameHeaderAliases = [
  'supervisorName',
  'supervisor',
  'managerName',
  '主管',
  '主管姓名',
  '直屬主管',
] as const

export type SalesUsersCsvParseOptions = {
  supervisorCsv?: string
}

export type SalesUsersCsvInspection = {
  headerCount: number
  hasEmail: boolean
  missingRequired: string[]
  rowErrors: string[]
}

const requiredInternalSalesEmails = [
  'sales1-manager@miao-form.case4.app',
  'sales1@miao-form.case4.app',
  'sales1-agent@miao-form.case4.app',
] as const

const internalSalesTeam = {
  id: 'internal-sales-team',
  name: '內部業務團隊',
  code: 'INTERNAL_SALES',
  departmentName: '內部業務部',
  departmentCode: 'INTERNAL_SALES',
} as const

export const miaolinCustomerFormTitle = '苗林實業客戶資料建檔'
const miaolinCustomerFormId = MIAOLIN_CUSTOMER_ONBOARDING_FORM_ID

export function isRequiredInternalSalesEmail(email: string | null | undefined) {
  const normalizedEmail = normalizeEmail(email)
  return requiredInternalSalesEmails.some((requiredEmail) => requiredEmail === normalizedEmail)
}

function isLegacyMockSalesUser(
  user: Pick<SalesUser, 'id' | 'email' | 'name' | 'teamName' | 'notes'>,
) {
  const normalizedEmail = normalizeEmail(user.email)
  if (/^sales[23](?:-|@)/.test(normalizedEmail)) return true
  if (/^(mia|ariel|nina|howard|yuna)\./.test(normalizedEmail)) return true
  if (user.id.startsWith('sales-import-')) return true
  if (/^Team [123] /.test(user.name)) return true
  if (/測試業務團隊 [123]/.test(user.teamName)) return true
  return user.notes.includes('mock-safe 業務匯入')
}

export function normalizeSalesUsersForInternalSeed(users: SalesUser[]) {
  const internalByEmail = new Set<string>(requiredInternalSalesEmails)
  const nonMockUsers = users.filter((user) => !isLegacyMockSalesUser(user))
  const persistedInternalByEmail = new Map(
    nonMockUsers
      .filter((user) => internalByEmail.has(normalizeEmail(user.email)))
      .map((user) => [normalizeEmail(user.email), user]),
  )
  const internalUsers = initialSalesUsers.map((internalUser) => {
    const persisted = persistedInternalByEmail.get(normalizeEmail(internalUser.email))
    if (!persisted) return internalUser
    return {
      ...internalUser,
      ...persisted,
      email: internalUser.email,
      teamId: persisted.teamId || internalUser.teamId,
      teamName: persisted.teamName || internalUser.teamName,
      teamCode: persisted.teamCode || internalUser.teamCode,
      departmentName: persisted.departmentName || internalUser.departmentName,
      departmentCode: persisted.departmentCode || internalUser.departmentCode,
      role: internalUser.role,
      employeeId: persisted.employeeId || internalUser.employeeId,
      supervisorEmail: persisted.supervisorEmail || internalUser.supervisorEmail,
      supervisorName: persisted.supervisorName || internalUser.supervisorName,
    }
  })
  const retainedFormalUsers = nonMockUsers.filter(
    (user) => !internalByEmail.has(normalizeEmail(user.email)),
  )
  return [
    ...internalUsers,
    ...retainedFormalUsers.filter(
      (user) =>
        !internalUsers.some(
          (internalUser) => normalizeEmail(internalUser.email) === normalizeEmail(user.email),
        ),
    ),
  ]
}

export type QuestionnaireAnswer = {
  id: string
  questionTitle: string
  questionType: string
  answer: string
  mappedField: string
}

export type CustomerApplication = {
  id: string
  formId: string
  formTitle: string
  formDescription: string
  submittedByEmail: string
  submittedByName: string
  supervisorEmail: string
  supervisorName: string
  teamId: string
  teamName: string
  status: ApplicationStatus
  companyName: string
  taxId: string
  industry: string
  customerType?: string
  departmentName?: string
  companySize: string
  website: string
  companyAddress?: string
  companyPhone?: string
  contactName: string
  contactTitle: string
  contactEmail: string
  contactPhone: string
  decisionRole?: string
  paymentTerms?: string
  creditLimit?: number
  salesRegion?: string
  expectedValue: number
  source: string
  needSummary: string
  internalNotes: string
  submittedAt: string
  reviewedAt: string
  reviewerEmail: string
  reviewerName: string
  reviewNotes: string
  answers: QuestionnaireAnswer[]
}

export type ManagedCustomer = {
  id: string
  sourceApplicationId: string
  formId: string
  formTitle: string
  companyName: string
  taxId: string
  industry: string
  customerType?: string
  departmentName?: string
  companySize: string
  website: string
  companyAddress?: string
  companyPhone?: string
  contactName: string
  contactTitle: string
  contactEmail: string
  contactPhone: string
  decisionRole?: string
  paymentTerms?: string
  creditLimit?: number
  salesRegion?: string
  ownerEmail: string
  ownerName: string
  supervisorEmail: string
  supervisorName: string
  teamId: string
  teamName: string
  status: CustomerRecordStatus
  customerDataStatus?: CustomerDataStatus
  filingStatus: CustomerFilingStatus
  erpCustomerCode: string
  erpFiledAt: string
  erpFiledBy: string
  expectedValue: number
  source: string
  needSummary: string
  internalNotes: string
  reviewNotes: string
  createdAt: string
  updatedAt: string
  answers: QuestionnaireAnswer[]
  auditEvents?: CustomerAuditTimelineEvent[]
}

export type WorkflowChecklistItem = {
  id: string
  label: string
  description: string
  passed: boolean
  severity: 'info' | 'warning' | 'danger'
}

export type CustomerAuditTimelineEvent = {
  id: string
  label: string
  description: string
  at: string
  actor: string
  tone: 'neutral' | 'success' | 'warning' | 'danger'
}

export type CustomerChangeState = 'new' | 'changed' | 'same' | 'empty'

export type CustomerChangeComparison = {
  field: string
  label: string
  applicationValue: string
  customerValue: string
  state: CustomerChangeState
}

type LegacyCustomerComparisonField = keyof Pick<
  CustomerApplication,
  | 'companyName'
  | 'taxId'
  | 'industry'
  | 'companySize'
  | 'website'
  | 'companyAddress'
  | 'companyPhone'
  | 'contactName'
  | 'contactTitle'
  | 'contactEmail'
  | 'contactPhone'
  | 'decisionRole'
  | 'paymentTerms'
  | 'creditLimit'
  | 'salesRegion'
  | 'expectedValue'
  | 'needSummary'
  | 'internalNotes'
>

export type ApplicationCustomerMatch = {
  customer: ManagedCustomer
  reasons: string[]
  source: 'source_application' | 'duplicate_signal'
}

export const applicationStatusMeta: Record<
  ApplicationStatus,
  { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  draft: { label: '草稿', variant: 'secondary' },
  pending_review: { label: '待主管審核', variant: 'warning' },
  changes_requested: { label: '主管退回', variant: 'default' },
  approved: { label: '主管核准', variant: 'success' },
  approved_pending_erp_filing: { label: '已核准，待 ERP 建檔', variant: 'success' },
  customer_created: { label: '客戶已建檔', variant: 'success' },
  duplicate_pending: { label: '重複待處理', variant: 'warning' },
  rejected: { label: '已拒絕', variant: 'destructive' },
}

export const customerDataStatusMeta: Record<
  CustomerDataStatus,
  { label: string; variant: 'success' | 'warning' }
> = {
  updated: { label: '已更新客戶資料', variant: 'success' },
  manual_review: { label: '需人工確認客戶資料', variant: 'warning' },
}

export function isReviewableApplicationStatus(status: ApplicationStatus) {
  return status === 'pending_review' || status === 'duplicate_pending'
}

function isApprovedApplicationStatus(status: ApplicationStatus | undefined) {
  return (
    status === 'approved' ||
    status === 'approved_pending_erp_filing' ||
    status === 'customer_created'
  )
}

export const customerFilingStatusMeta: Record<
  CustomerFilingStatus,
  { label: string; variant: 'warning' | 'success' }
> = {
  not_filed: { label: 'ERP未建檔', variant: 'warning' },
  filed: { label: 'ERP已建檔', variant: 'success' },
}

export const initialSalesUsers: SalesUser[] = [
  {
    id: 'sales1-manager',
    email: 'sales1-manager@miao-form.case4.app',
    name: '內部業務主管',
    employeeId: 'INTERNAL_SALES_MANAGER',
    title: '業務主管',
    phone: '',
    teamId: internalSalesTeam.id,
    teamName: internalSalesTeam.name,
    teamCode: internalSalesTeam.code,
    departmentName: internalSalesTeam.departmentName,
    departmentCode: internalSalesTeam.departmentCode,
    role: 'manager',
    supervisorEmail: 'sales1-manager@miao-form.case4.app',
    supervisorName: '內部業務主管',
    status: 'active',
    territory: '',
    customerSegment: 'Internal',
    notes:
      '內部業務主管身份；登入帳號與密碼只能由 INTERNAL_SALES_MANAGER_ACCOUNT / INTERNAL_SALES_MANAGER_PASSWORD 設定。',
  },
  {
    id: 'sales1',
    email: 'sales1@miao-form.case4.app',
    name: '內部業務',
    employeeId: 'INTERNAL_SALES',
    title: '業務',
    phone: '',
    teamId: internalSalesTeam.id,
    teamName: internalSalesTeam.name,
    teamCode: internalSalesTeam.code,
    departmentName: internalSalesTeam.departmentName,
    departmentCode: internalSalesTeam.departmentCode,
    role: 'sales',
    supervisorEmail: 'sales1-manager@miao-form.case4.app',
    supervisorName: '內部業務主管',
    status: 'active',
    territory: '',
    customerSegment: 'Internal',
    notes:
      '內部業務身份；登入帳號與密碼只能由 INTERNAL_SALES_ACCOUNT / INTERNAL_SALES_PASSWORD 設定。',
  },
  {
    id: 'sales1-agent',
    email: 'sales1-agent@miao-form.case4.app',
    name: '內部業務助理',
    employeeId: 'INTERNAL_SALES_ASSISTANT',
    title: '業務助理',
    phone: '',
    teamId: internalSalesTeam.id,
    teamName: internalSalesTeam.name,
    teamCode: internalSalesTeam.code,
    departmentName: internalSalesTeam.departmentName,
    departmentCode: internalSalesTeam.departmentCode,
    role: 'assistant',
    supervisorEmail: 'sales1-manager@miao-form.case4.app',
    supervisorName: '內部業務主管',
    status: 'active',
    territory: '',
    customerSegment: 'Internal',
    notes:
      '內部業務助理身份；登入帳號與密碼只能由 INTERNAL_SALES_ASSISTANT_ACCOUNT / INTERNAL_SALES_ASSISTANT_PASSWORD 設定。',
  },
]

export const initialApplications: CustomerApplication[] = []

export const initialCustomers: ManagedCustomer[] = []

export function getSalesUserByEmail(email: string | null | undefined, users = initialSalesUsers) {
  const normalizedEmail = normalizeEmail(email)
  return users.find((user) => normalizeEmail(user.email) === normalizedEmail)
}

export function getBusinessRole(
  user:
    | (Pick<SessionUser, 'email' | 'role'> &
        Partial<Pick<SessionUser, 'productionRole' | 'businessRole'>>)
    | null
    | undefined,
  users = initialSalesUsers,
): BusinessRole {
  if (user?.role === 'owner' || user?.role === 'admin' || isAdminEmail(user?.email)) return 'admin'
  if (user?.productionRole === 'super_admin' || user?.productionRole === 'admin') return 'admin'
  if (user?.productionRole === 'sales_manager' || user?.businessRole === 'manager') return 'manager'
  if (user?.productionRole === 'sales_assistant' || user?.businessRole === 'assistant')
    return 'assistant'
  if (user?.productionRole === 'sales' || user?.businessRole === 'sales') return 'sales'
  const salesUser = getSalesUserByEmail(user?.email, users)
  if (!salesUser || salesUser.status !== 'active') return 'none'
  if (salesUser.role === 'manager') return 'manager'
  if (salesUser.role === 'assistant') return 'assistant'
  return 'sales'
}

export function isSalesContributorRole(role: SalesUserRole) {
  return role === 'sales' || role === 'assistant'
}

export function canAccessSalesWorkspace(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  return getBusinessRole(user, users) !== 'none'
}

export function canReviewApplications(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  const role = getBusinessRole(user, users)
  return role === 'admin' || role === 'manager'
}

export function canAccessCustomerApplications(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  const role = getBusinessRole(user, users)
  return role === 'admin' || role === 'manager' || role === 'sales' || role === 'assistant'
}

export function canAccessReviewCenter(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  return canReviewApplications(user, users)
}

export function canAccessCustomerManagement(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  return canAccessSalesWorkspace(user, users)
}

export function canBackfillCustomerData(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  users = initialSalesUsers,
) {
  const role = getBusinessRole(user, users)
  return role === 'admin' || role === 'manager' || role === 'sales' || role === 'assistant'
}

export function normalizeEmail(email: string | null | undefined) {
  return email?.trim().toLocaleLowerCase() ?? ''
}

function normalizeSalesUserRole(role: string | undefined): SalesUserRole {
  const normalized = role?.trim().toLocaleLowerCase()
  if (normalized === 'manager' || normalized === '業務主管' || normalized === '主管')
    return 'manager'
  if (normalized === 'assistant' || normalized === 'agent' || normalized === '業務助理')
    return 'assistant'
  return 'sales'
}

function normalizeSalesUserStatus(status: string | undefined): SalesUserStatus {
  const normalized = status?.trim().toLocaleLowerCase()
  if (normalized === 'blocked' || normalized === '封鎖') return 'blocked'
  if (
    normalized === 'inactive' ||
    normalized === 'disabled' ||
    normalized === '停用' ||
    normalized === '離職'
  )
    return 'inactive'
  return 'active'
}

function normalizeSalesUserActiveFlag(value: string | undefined) {
  const normalized = value?.trim().toLocaleLowerCase()
  if (!normalized) return undefined
  if (['true', '1', 'yes', 'y', 'active', '啟用', '在職'].includes(normalized)) return true
  if (['false', '0', 'no', 'n', 'inactive', 'disabled', '停用', '離職'].includes(normalized))
    return false
  return undefined
}

function isSupportedSalesUserImportRole(role: string) {
  if (!role.trim()) return true
  const normalized = role.trim().toLocaleLowerCase()
  return salesUserImportRoleAliases.some((alias) => alias.toLocaleLowerCase() === normalized)
}

function isSupportedSalesUserImportStatus(status: string) {
  if (!status.trim()) return true
  const normalized = status.trim().toLocaleLowerCase()
  return salesUserImportStatusAliases.some((alias) => alias.toLocaleLowerCase() === normalized)
}

function toHeaderKey(value: string) {
  return value
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[_\s-]/g, '')
    .toLocaleLowerCase()
}

function hasSalesUserImportEmailHeader(headers: string[]) {
  return salesUserImportEmailHeaderAliases.some((key) => headers.includes(toHeaderKey(key)))
}

function getImportCell(row: Record<string, string>, keys: ReadonlyArray<string>) {
  for (const key of keys) {
    const value = row[toHeaderKey(key)]
    if (value !== undefined) return value.trim()
  }
  return ''
}

function createImportSlug(value: string, fallback: string) {
  return (
    value
      .trim()
      .toLocaleLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || fallback
  )
}

function parseCsvRows(csv: string) {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  function pushField() {
    row.push(field.trim())
    field = ''
  }

  function pushRow() {
    pushField()
    if (row.some((cell) => cell.trim())) rows.push(row)
    row = []
  }

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index]
    const next = csv[index + 1]
    if (char === '"') {
      if (inQuotes && next === '"') {
        field += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (char === ',' && !inQuotes) {
      pushField()
      continue
    }
    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1
      pushRow()
      continue
    }
    field += char
  }

  if (field || row.length > 0) pushRow()
  return rows
}

function parseCsvTable(csv: string) {
  const rows = parseCsvRows(csv)
  const [headerRow, ...bodyRows] = rows
  if (!headerRow) return { headers: [] as string[], rows: [] as Record<string, string>[] }
  const headers = headerRow.map(toHeaderKey)
  return {
    headers,
    rows: bodyRows.map((bodyRow) =>
      Object.fromEntries(
        headers.map((header, headerIndex) => [header, bodyRow[headerIndex] ?? '']),
      ),
    ),
  }
}

function findImportHeaderIndex(headers: string[], keys: ReadonlyArray<string>) {
  return headers.findIndex((header) => keys.some((key) => header === toHeaderKey(key)))
}

function createImportedSalesUser(row: Record<string, string>, index: number): SalesUser | null {
  const email = normalizeEmail(getImportCell(row, salesUserImportEmailHeaderAliases))
  const name = getImportCell(row, salesUserImportNameHeaderAliases) || email.split('@')[0]
  const role = normalizeSalesUserRole(getImportCell(row, ['role', '角色']))
  const departmentName = getImportCell(row, [
    'departmentName',
    'department',
    'department_name',
    '部門別',
  ])
  const departmentCode = getImportCell(row, [
    'departmentCode',
    'department_code',
    'departmentId',
    '部門代號',
  ])
  const teamName =
    getImportCell(row, ['teamName', 'team', 'team_name', '團隊', '部門']) ||
    departmentName ||
    '匯入業務團隊'
  const teamId =
    getImportCell(row, ['teamId', 'team_id', '團隊代碼']) ||
    (departmentCode
      ? `department-${createImportSlug(departmentCode, String(index + 1))}`
      : `team-${createImportSlug(teamName, String(index + 1))}`)
  const teamCode =
    getImportCell(row, salesUserImportTeamCodeHeaderAliases) || extractFirstNumericToken(teamName)
  const salesCode = getImportCell(row, salesUserImportSalesCodeHeaderAliases)
  const supervisorEmail = normalizeEmail(
    getImportCell(row, ['supervisorEmail', 'supervisor_email', 'managerEmail', '主管email']),
  )
  const supervisorName = getImportCell(row, salesUserImportSupervisorNameHeaderAliases)
  const notes = getImportCell(row, ['notes', '備註'])
  const statusValue = getImportCell(row, ['status', '狀態'])
  const activeFlag = normalizeSalesUserActiveFlag(
    getImportCell(row, ['is_active', 'isActive', 'active', '啟用狀態']),
  )
  const statusSpecified = Boolean(statusValue) || activeFlag !== undefined
  const status = statusValue
    ? normalizeSalesUserStatus(statusValue)
    : activeFlag === undefined
      ? 'active'
      : activeFlag
        ? 'active'
        : 'inactive'

  if (!email) return null

  return {
    id: `sales-import-${email.replace(/[^a-z0-9]+/g, '-') || index}`,
    email,
    name,
    employeeId: salesCode,
    title: getImportCell(row, ['title', '職稱']) || (role === 'manager' ? '業務主管' : '業務'),
    phone: getImportCell(row, ['phone', '電話']),
    teamId,
    teamName,
    teamCode,
    departmentName,
    departmentCode,
    role,
    supervisorEmail: role === 'manager' ? email : supervisorEmail,
    supervisorName,
    status,
    statusSpecified,
    territory: getImportCell(row, ['territory', '區域']),
    customerSegment: getImportCell(row, ['customerSegment', '客群']),
    notes: notes || '由業務名單匯入建立。',
  }
}

function extractFirstNumericToken(value: string) {
  return value.match(/\d{1,8}/)?.[0] ?? ''
}

type SupervisorMapping = {
  employeeName: string
  supervisorName: string
  supervisorEmail: string
}

function parseSupervisorMappings(csv?: string) {
  if (!csv?.trim()) return { mappings: [] as SupervisorMapping[], errors: [] as string[] }
  const table = parseCsvTable(csv)
  if (table.headers.length === 0) {
    return { mappings: [] as SupervisorMapping[], errors: ['缺少直屬主管 CSV header。'] }
  }
  const employeeNameIndex = findImportHeaderIndex(table.headers, salesUserImportNameHeaderAliases)
  const supervisorNameIndex = findImportHeaderIndex(
    table.headers,
    salesUserImportSupervisorNameHeaderAliases,
  )
  if (employeeNameIndex < 0 || supervisorNameIndex < 0) {
    return {
      mappings: [] as SupervisorMapping[],
      errors: ['直屬主管 CSV 需要包含業務簡稱與直屬主管欄位。'],
    }
  }
  return {
    mappings: table.rows
      .map((row) => ({
        employeeName: getImportCell(row, salesUserImportNameHeaderAliases),
        supervisorName: getImportCell(row, salesUserImportSupervisorNameHeaderAliases),
        supervisorEmail: normalizeEmail(
          getImportCell(row, ['supervisorEmail', 'supervisor_email', 'managerEmail', '主管email']),
        ),
      }))
      .filter((mapping) => mapping.employeeName && mapping.supervisorName),
    errors: [] as string[],
  }
}

function normalizeSalesImportName(value: string) {
  return value.trim().toLocaleLowerCase()
}

function applySupervisorMappingsToImportedUsers(users: SalesUser[], mappings: SupervisorMapping[]) {
  if (mappings.length === 0) return users
  const byName = new Map(users.map((user) => [normalizeSalesImportName(user.name), user]))
  const managerNameKeys = new Set(
    mappings.map((mapping) => normalizeSalesImportName(mapping.supervisorName)).filter(Boolean),
  )
  const mappedEmployeeKeys = new Set(
    mappings.map((mapping) => normalizeSalesImportName(mapping.employeeName)).filter(Boolean),
  )
  const next = users.map((user) => {
    const key = normalizeSalesImportName(user.name)
    const mapped = mappings.find(
      (mapping) => normalizeSalesImportName(mapping.employeeName) === key,
    )
    const supervisor = mapped ? byName.get(normalizeSalesImportName(mapped.supervisorName)) : null
    const isManager = managerNameKeys.has(key)
    const role = isManager ? 'manager' : user.role
    return {
      ...user,
      role,
      supervisorEmail: mapped
        ? (supervisor?.email ?? mapped.supervisorEmail)
        : isManager && !user.supervisorEmail
          ? user.email
          : user.supervisorEmail,
      supervisorName: mapped
        ? (supervisor?.name ?? mapped.supervisorName)
        : isManager && !user.supervisorName
          ? user.name
          : user.supervisorName,
    }
  })

  return next.map((user) => {
    if (user.role !== 'manager') return user
    const hasExplicitSupervisor = mappedEmployeeKeys.has(normalizeSalesImportName(user.name))
    return {
      ...user,
      supervisorEmail: user.supervisorEmail || (hasExplicitSupervisor ? '' : user.email),
      supervisorName: user.supervisorName || (hasExplicitSupervisor ? '' : user.name),
    }
  })
}

export function inspectSalesUsersCsvContent(csv: string): SalesUsersCsvInspection | null {
  const table = parseCsvTable(csv)
  if (table.headers.length === 0) return null
  const hasEmail = hasSalesUserImportEmailHeader(table.headers)
  const roleIndex = findImportHeaderIndex(table.headers, ['role', '角色'])
  const statusIndex = findImportHeaderIndex(table.headers, ['status', '狀態'])
  const rowErrors = table.rows.flatMap((row, bodyIndex) => {
    const role = roleIndex >= 0 ? row[table.headers[roleIndex]] : ''
    const status = statusIndex >= 0 ? row[table.headers[statusIndex]] : ''
    const errors: string[] = []
    if (role && !isSupportedSalesUserImportRole(role)) {
      errors.push(`第 ${bodyIndex + 2} 列 role 不支援：${role}。`)
    }
    if (status && !isSupportedSalesUserImportStatus(status)) {
      errors.push(`第 ${bodyIndex + 2} 列 status 不支援：${status}。`)
    }
    return errors
  })
  return {
    headerCount: table.headers.length,
    hasEmail,
    missingRequired: hasEmail ? [] : ['email'],
    rowErrors,
  }
}

export function parseSalesUsersCsv(csv: string, options: SalesUsersCsvParseOptions = {}) {
  const table = parseCsvTable(csv)
  if (table.headers.length === 0) return { users: [] as SalesUser[], errors: ['缺少 CSV header。'] }

  const headers = table.headers
  if (!hasSalesUserImportEmailHeader(headers)) {
    return { users: [] as SalesUser[], errors: ['缺少必要欄位 email。'] }
  }

  const users: SalesUser[] = []
  const errors: string[] = []

  table.rows.forEach((row, index) => {
    const roleValue = getImportCell(row, ['role', '角色'])
    const statusValue = getImportCell(row, ['status', '狀態'])
    const rowErrors: string[] = []
    if (!isSupportedSalesUserImportRole(roleValue)) {
      rowErrors.push(`第 ${index + 2} 列 role 不支援：${roleValue}。`)
    }
    if (!isSupportedSalesUserImportStatus(statusValue)) {
      rowErrors.push(`第 ${index + 2} 列 status 不支援：${statusValue}。`)
    }
    if (rowErrors.length > 0) {
      errors.push(...rowErrors)
      return
    }
    const user = createImportedSalesUser(row, index)
    if (!user) {
      errors.push(`第 ${index + 2} 列缺少 email。`)
      return
    }
    users.push(user)
  })

  const supervisorMappings = parseSupervisorMappings(options.supervisorCsv)
  errors.push(...supervisorMappings.errors)

  return {
    users:
      errors.length > 0
        ? users
        : applySupervisorMappingsToImportedUsers(users, supervisorMappings.mappings),
    errors,
  }
}

export function applyAppendOnlySalesUserImport(
  currentUsers: SalesUser[],
  importedUsers: SalesUser[],
): SalesUserImportResult {
  const importedByEmail = new Map(importedUsers.map((user) => [normalizeEmail(user.email), user]))
  const currentByEmail = new Map(currentUsers.map((user) => [normalizeEmail(user.email), user]))
  const nextUsers: SalesUser[] = [...currentUsers]
  let created = 0
  let skippedExisting = 0

  for (const imported of importedUsers) {
    const normalizedEmail = normalizeEmail(imported.email)
    const current = currentByEmail.get(normalizedEmail)
    if (current) {
      skippedExisting += 1
      continue
    }

    const supervisor =
      imported.role === 'manager'
        ? imported
        : (importedByEmail.get(normalizeEmail(imported.supervisorEmail)) ??
          currentByEmail.get(normalizeEmail(imported.supervisorEmail)))

    const createdUser: SalesUser = {
      ...imported,
      status: 'active',
      supervisorName:
        imported.role === 'manager' ? imported.name : (supervisor?.name ?? imported.supervisorName),
    }
    nextUsers.push(createdUser)
    currentByEmail.set(normalizedEmail, createdUser)
    created += 1
  }

  return {
    salesUsers: nextUsers,
    summary: {
      imported: importedUsers.length,
      created,
      skippedExisting,
      errors: [],
    },
  }
}

export function applicationToCustomer(
  application: CustomerApplication,
  options: {
    reviewerEmail: string
    reviewerName: string
    filingStatus?: CustomerFilingStatus
    erpCustomerCode?: string
    erpFiledAt?: string
    erpFiledBy?: string
  },
): ManagedCustomer {
  const now = new Date().toISOString().slice(0, 10)
  const customerChannel = getApplicationAnswerValue(application, 'customerChannel', 'industry')
  const customerType = getApplicationAnswerValue(application, 'customerType')
  const departmentName = getApplicationAnswerValue(application, 'department', 'departmentName')
  const companySize = getApplicationAnswerValue(
    application,
    'employeeCountRange',
    'companySize',
  )
  return {
    id: `cus-${application.id}`,
    sourceApplicationId: application.id,
    formId: application.formId,
    formTitle: application.formTitle,
    companyName: application.companyName,
    taxId: application.taxId,
    industry: customerChannel || application.industry,
    customerType: customerType || application.customerType || '',
    departmentName: departmentName || application.departmentName || '',
    companySize: companySize || application.companySize,
    website: application.website,
    companyAddress: application.companyAddress ?? '',
    companyPhone: application.companyPhone ?? '',
    contactName: application.contactName,
    contactTitle: application.contactTitle,
    contactEmail: application.contactEmail,
    contactPhone: application.contactPhone,
    decisionRole: application.decisionRole ?? '',
    paymentTerms: application.paymentTerms ?? '',
    creditLimit: application.creditLimit ?? 0,
    salesRegion: application.salesRegion ?? '',
    ownerEmail: application.submittedByEmail,
    ownerName: application.submittedByName,
    supervisorEmail: application.supervisorEmail,
    supervisorName: application.supervisorName,
    teamId: application.teamId,
    teamName: application.teamName,
    status: 'active',
    customerDataStatus: 'updated',
    filingStatus: options.filingStatus ?? 'not_filed',
    erpCustomerCode: options.erpCustomerCode ?? '',
    erpFiledAt: options.erpFiledAt ?? '',
    erpFiledBy: options.erpFiledBy ?? '',
    expectedValue: application.expectedValue,
    source: application.source,
    needSummary: application.needSummary,
    internalNotes: application.internalNotes,
    reviewNotes:
      application.reviewNotes ||
      `由 ${options.reviewerName} 核准後已更新客戶資料，ERP 建檔仍待人工確認。`,
    createdAt: now,
    updatedAt: now,
    answers: application.answers,
  }
}

function getApplicationAnswerValue(
  application: Pick<CustomerApplication, 'answers'>,
  ...mappedFields: string[]
) {
  const normalizedFields = new Set(mappedFields.map(normalizeComparableValue))
  const matchedAnswer = application.answers.find((answer) =>
    normalizedFields.has(normalizeComparableValue(answer.mappedField)),
  )
  return matchedAnswer?.answer?.trim() ?? ''
}

function normalizeComparableValue(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, '')
}

export function applicationToExistingCustomer(
  application: CustomerApplication,
  existingCustomer: ManagedCustomer,
  options: {
    reviewerEmail: string
    reviewerName: string
  },
): ManagedCustomer {
  const now = new Date().toISOString().slice(0, 10)
  const customerChannel = getApplicationAnswerValue(application, 'customerChannel', 'industry')
  const customerType = getApplicationAnswerValue(application, 'customerType')
  const departmentName = getApplicationAnswerValue(application, 'department', 'departmentName')
  const companySize = getApplicationAnswerValue(
    application,
    'employeeCountRange',
    'companySize',
  )
  return {
    ...existingCustomer,
    sourceApplicationId: application.id,
    formId: application.formId,
    formTitle: application.formTitle,
    companyName: application.companyName,
    taxId: application.taxId,
    industry: customerChannel || application.industry,
    customerType: customerType || application.customerType || '',
    departmentName: departmentName || application.departmentName || '',
    companySize: companySize || application.companySize,
    website: application.website,
    companyAddress: application.companyAddress ?? '',
    companyPhone: application.companyPhone ?? '',
    contactName: application.contactName,
    contactTitle: application.contactTitle,
    contactEmail: application.contactEmail,
    contactPhone: application.contactPhone,
    decisionRole: application.decisionRole ?? '',
    paymentTerms: application.paymentTerms ?? '',
    creditLimit: application.creditLimit ?? 0,
    salesRegion: application.salesRegion ?? '',
    ownerEmail: application.submittedByEmail,
    ownerName: application.submittedByName,
    supervisorEmail: application.supervisorEmail,
    supervisorName: application.supervisorName,
    teamId: application.teamId,
    teamName: application.teamName,
    status: 'active',
    customerDataStatus: 'updated',
    filingStatus: 'not_filed',
    expectedValue: application.expectedValue,
    source: application.source,
    needSummary: application.needSummary,
    internalNotes: application.internalNotes,
    reviewNotes:
      application.reviewNotes ||
      `由 ${options.reviewerName} 核准後已更新既有客戶資料，ERP 建檔仍待人工確認。`,
    updatedAt: now,
    answers: application.answers,
  }
}

export function getCustomerForApplication(
  applicationId: string,
  customers: Array<
    Pick<ManagedCustomer, 'sourceApplicationId' | 'filingStatus' | 'id' | 'companyName'>
  >,
) {
  return customers.find((customer) => customer.sourceApplicationId === applicationId)
}

function normalizeComparable(value: string | number | undefined) {
  return String(value ?? '').trim()
}

function sameText(left: string | undefined, right: string | undefined) {
  return (
    normalizeComparable(left).toLocaleLowerCase() === normalizeComparable(right).toLocaleLowerCase()
  )
}

export function getApplicationCustomerMatch(
  application: Pick<
    CustomerApplication,
    'id' | 'taxId' | 'companyName' | 'contactEmail' | 'contactPhone'
  >,
  customers: ManagedCustomer[],
): ApplicationCustomerMatch | null {
  const sourceCustomer = customers.find(
    (customer) => customer.sourceApplicationId === application.id,
  )
  if (sourceCustomer) {
    return {
      customer: sourceCustomer,
      reasons: ['來源申請已更新客戶資料'],
      source: 'source_application',
    }
  }

  const scored = customers
    .map((customer) => {
      const reasons: string[] = []
      if (application.taxId && customer.taxId === application.taxId) reasons.push('統編相同')
      if (application.companyName && sameText(customer.companyName, application.companyName)) {
        reasons.push('公司名稱相同')
      }
      return { customer, reasons }
    })
    .filter((item) => item.reasons.length > 0)
    .sort((left, right) => right.reasons.length - left.reasons.length)

  const [first] = scored
  return first
    ? {
        customer: first.customer,
        reasons: first.reasons,
        source: 'duplicate_signal',
      }
    : null
}

export function getApplicationCustomerStatusLabel(
  application: Pick<
    CustomerApplication,
    'id' | 'taxId' | 'companyName' | 'contactEmail' | 'contactPhone' | 'status'
  >,
  customers: ManagedCustomer[],
) {
  if (application.status === 'rejected' || application.status === 'changes_requested') {
    return {
      label: '未更新客戶資料',
      description: '申請未核准，不會建立或更新客戶資料。',
    }
  }
  const match = getApplicationCustomerMatch(application, customers)
  if (!match) return { label: '尚未更新客戶資料', description: '尚未找到已更新的客戶資料。' }
  return {
    label: match.source === 'source_application' ? '已更新客戶資料' : '可能已有客戶',
    description: `${match.customer.companyName}：${match.reasons.join('、')}`,
  }
}

export function getCustomerDataStatus(customer: Pick<ManagedCustomer, 'customerDataStatus'>) {
  return customer.customerDataStatus ?? 'updated'
}

export function getApplicationFormTypeAnswer(
  application: Pick<CustomerApplication, 'answers' | 'source'>,
) {
  return (
    findApplicationAnswer(application, '新客戶建檔選擇') ||
    findApplicationAnswer(application, '流程控制 / 新客戶建檔選擇') ||
    application.source ||
    ''
  )
}

export function getApplicationSecondaryFlowAnswer(
  application: Pick<CustomerApplication, 'answers' | 'source'>,
) {
  const formType = getApplicationFormTypeAnswer(application)
  if (isFullNewCustomerApplication(application)) {
    return findApplicationAnswer(application, '新客戶來源')
  }
  if (isExistingCustomerChangeApplication(application)) {
    return findApplicationAnswer(application, '資料變更選擇')
  }
  return formType === application.source ? '' : application.source
}

export function summarizeApplicationFormType(
  application: Pick<CustomerApplication, 'answers' | 'source'>,
) {
  const formType = getApplicationFormTypeAnswer(application)
  const secondary = getApplicationSecondaryFlowAnswer(application)
  if (formType && secondary) return `${formType} / ${secondary}`
  return formType || secondary || '-'
}

export function isFullNewCustomerApplication(
  application: Pick<CustomerApplication, 'answers' | 'source'> | null | undefined,
) {
  return Boolean(
    application &&
      normalizeFlowAnswer(getApplicationFormTypeAnswer(application)).includes(
        normalizeFlowAnswer('全新客戶'),
      ),
  )
}

export function isExistingCustomerChangeApplication(
  application: Pick<CustomerApplication, 'answers' | 'source'> | null | undefined,
) {
  return Boolean(
    application &&
      normalizeFlowAnswer(getApplicationFormTypeAnswer(application)).includes(
        normalizeFlowAnswer('既有客戶變更資料'),
      ),
  )
}

function findApplicationAnswer(application: Pick<CustomerApplication, 'answers'>, title: string) {
  const titleKey = normalizeFlowAnswer(title)
  return (
    application.answers.find((answer) =>
      normalizeFlowAnswer(answer.questionTitle).includes(titleKey),
    )?.answer ?? ''
  )
}

function normalizeFlowAnswer(value: string | undefined) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, '')
}

const customerComparisonFields: Array<{
  field: LegacyCustomerComparisonField
  label: string
}> = [
  { field: 'companyName', label: '公司名稱' },
  { field: 'taxId', label: '統編 / Tax ID' },
  { field: 'industry', label: '產業 / 類別' },
  { field: 'companySize', label: '公司規模' },
  { field: 'website', label: '網站' },
  { field: 'companyAddress', label: '地址' },
  { field: 'companyPhone', label: '公司電話' },
  { field: 'contactName', label: '聯絡人' },
  { field: 'contactTitle', label: '職稱' },
  { field: 'contactEmail', label: 'Email' },
  { field: 'contactPhone', label: '電話' },
  { field: 'decisionRole', label: '決策角色' },
  { field: 'paymentTerms', label: '付款條件' },
  { field: 'creditLimit', label: '授信額度' },
  { field: 'salesRegion', label: '業務區域' },
  { field: 'expectedValue', label: '預估成交金額' },
  { field: 'needSummary', label: '需求摘要' },
  { field: 'internalNotes', label: '內部備註' },
]

export function getCustomerChangeComparisons(
  application: CustomerApplication,
  existingCustomer?: ManagedCustomer | null,
): CustomerChangeComparison[] {
  if (application.answers.length > 0) {
    return getQuestionnaireAnswerComparisons(application.answers, existingCustomer?.answers ?? [])
  }

  return customerComparisonFields.map(({ field, label }) => {
    const applicationValue = normalizeComparable(application[field])
    const customerValue = normalizeComparable(existingCustomer?.[field])
    const state: CustomerChangeState = existingCustomer
      ? !applicationValue && !customerValue
        ? 'empty'
        : applicationValue === customerValue
          ? 'same'
          : 'changed'
      : applicationValue
        ? 'new'
        : 'empty'

    return {
      field,
      label,
      applicationValue,
      customerValue,
      state,
    }
  })
}

function getQuestionnaireAnswerComparisons(
  applicationAnswers: QuestionnaireAnswer[],
  customerAnswers: QuestionnaireAnswer[],
): CustomerChangeComparison[] {
  const customerAnswersByField = new Map(
    customerAnswers
      .filter((answer) => answer.mappedField)
      .map((answer) => [answer.mappedField, answer]),
  )
  const customerAnswersByTitle = new Map(
    customerAnswers.map((answer) => [answer.questionTitle, answer]),
  )

  return applicationAnswers.map((answer) => {
    const existingAnswer =
      customerAnswersByField.get(answer.mappedField) ??
      customerAnswersByTitle.get(answer.questionTitle)
    const applicationValue = normalizeComparable(answer.answer)
    const customerValue = normalizeComparable(existingAnswer?.answer)
    const state: CustomerChangeState = existingAnswer
      ? !applicationValue && !customerValue
        ? 'empty'
        : applicationValue === customerValue
          ? 'same'
          : 'changed'
      : applicationValue
        ? 'new'
        : 'empty'

    return {
      field: answer.id,
      label: answer.questionTitle,
      applicationValue,
      customerValue,
      state,
    }
  })
}

export function summarizeApplicationChange(
  application: CustomerApplication,
  existingCustomer?: ManagedCustomer | null,
) {
  const comparisons = getCustomerChangeComparisons(application, existingCustomer)
  const changed = comparisons.filter((item) => item.state === 'changed')
  const filledNew = comparisons.filter((item) => item.state === 'new')
  if (existingCustomer && changed.length > 0) {
    return `變更 ${changed.length} 欄：${changed
      .slice(0, 3)
      .map((item) => item.label)
      .join('、')}${changed.length > 3 ? '...' : ''}`
  }
  if (existingCustomer) return '既有客戶資料未偵測到主要欄位變更'
  if (filledNew.length > 0) {
    return `新增客戶資料 ${filledNew.length} 欄：${filledNew
      .slice(0, 3)
      .map((item) => item.label)
      .join('、')}${filledNew.length > 3 ? '...' : ''}`
  }
  return '尚未取得可建檔欄位'
}

export function getApplicationChecklist(
  application: CustomerApplication,
  options: {
    duplicateSignalsCount?: number
    duplicateDecision?: 'merge_preview' | 'new_customer'
    reviewNotes?: string
  } = {},
): WorkflowChecklistItem[] {
  const reviewNotes = options.reviewNotes ?? application.reviewNotes
  return [
    {
      id: 'required-fields',
      label: '必填欄位',
      description: '公司名稱、統編、聯絡人 Email 與需求摘要需可供 ERP 建檔前檢查。',
      passed: Boolean(
        application.companyName &&
          application.taxId &&
          application.contactEmail &&
          application.needSummary,
      ),
      severity: 'danger',
    },
    {
      id: 'duplicate-risk',
      label: '重複風險',
      description: '若命中統編、公司名稱、Email 或電話，需先選擇合併預覽或改為新客戶。',
      passed: (options.duplicateSignalsCount ?? 0) === 0 || Boolean(options.duplicateDecision),
      severity: 'warning',
    },
    {
      id: 'questionnaire-source',
      label: '問卷來源',
      description: '保留對應問卷、送出業務、主管與團隊，供後續 audit trail 使用。',
      passed: Boolean(
        application.formTitle &&
          application.submittedByEmail &&
          application.supervisorEmail &&
          application.teamId,
      ),
      severity: 'info',
    },
    {
      id: 'supervisor-notes',
      label: '主管審核意見',
      description: '核准或退回前建議留下審核理由，讓業務與 ERP 建檔人員可追溯。',
      passed: Boolean(reviewNotes.trim()),
      severity: 'info',
    },
  ]
}

export function getCustomerAuditTimeline(
  customer: ManagedCustomer,
  application?: CustomerApplication | null,
): CustomerAuditTimelineEvent[] {
  const events: CustomerAuditTimelineEvent[] = []
  const source = application ?? null
  const isExistingCustomerUpdate = Boolean(source && customer.id !== `cus-${source.id}`)
  const reviewCompleted = source ? isApprovedApplicationStatus(source.status) : true
  events.push({
    id: 'submitted',
    label: isExistingCustomerUpdate ? '送出既有客戶變更申請' : '送出新客戶申請',
    description: `${customer.ownerName} 透過「${customer.formTitle}」${
      isExistingCustomerUpdate ? '提出既有客戶資料變更。' : '建立新客戶申請。'
    }`,
    at: source?.submittedAt || customer.createdAt,
    actor: customer.ownerName,
    tone: 'neutral',
  })

  const reviewRejected = source?.status === 'changes_requested' || source?.status === 'rejected'

  if (source && reviewRejected && (source.reviewedAt || customer.reviewNotes)) {
    events.push({
      id: 'reviewed',
      label:
        source.status === 'changes_requested'
          ? '主管退回'
          : source.status === 'rejected'
            ? '主管拒絕'
            : isExistingCustomerUpdate
              ? '主管核准並更新客戶'
              : '主管核准並建立客戶',
      description: source?.reviewNotes || customer.reviewNotes || '已保留主管審核紀錄。',
      at: source?.reviewedAt || customer.createdAt,
      actor: source?.reviewerName || customer.supervisorName,
      tone:
        source.status === 'rejected'
          ? 'danger'
          : source.status === 'changes_requested'
            ? 'warning'
            : 'success',
    })
  } else if (source && reviewCompleted) {
    events.push({
      id: 'reviewed',
      label: isExistingCustomerUpdate ? '主管核准並更新客戶' : '主管核准並建立客戶',
      description: source?.reviewNotes || customer.reviewNotes || '主管已核准此申請。',
      at: source?.reviewedAt || customer.updatedAt || customer.createdAt,
      actor: source?.reviewerName || customer.supervisorName,
      tone: 'success',
    })
  } else if (source) {
    events.push({
      id: 'reviewed',
      label: isExistingCustomerUpdate ? '主管核准並更新客戶' : '主管核准並建立客戶',
      description: '待主管審核通過後才會完成。',
      at: source?.submittedAt || customer.createdAt,
      actor: customer.supervisorName,
      tone: 'warning',
    })
  }

  if (reviewRejected) return events

  events.push({
    id: 'customer-created',
    label: isExistingCustomerUpdate ? '已更新既有客戶資料' : '已更新客戶資料',
    description: reviewCompleted
      ? isExistingCustomerUpdate
        ? '主管核准後更新既有客戶資料，保留問卷與審核來源。'
        : '主管核准後建立客戶資料，保留問卷與審核來源。'
      : isExistingCustomerUpdate
        ? '待主管核准後才會更新既有客戶資料。'
        : '待主管核准後才會建立或更新客戶資料。',
    at: isExistingCustomerUpdate ? customer.updatedAt : customer.createdAt,
    actor: customer.supervisorName,
    tone: reviewCompleted ? 'success' : 'warning',
  })

  events.push(...(customer.auditEvents ?? []))

  events.push({
    id: 'erp-filing',
    label: customer.filingStatus === 'filed' ? 'ERP已建檔' : 'ERP未建檔',
    description:
      customer.filingStatus === 'filed'
        ? `ERP 客戶代號 ${customer.erpCustomerCode || '尚未填寫'}，建檔人 ${customer.erpFiledBy || '未記錄'}。`
        : '待業務或建檔人員到外部 ERP 手動建檔後回填狀態。',
    at: customer.erpFiledAt || customer.updatedAt,
    actor: customer.erpFiledBy || customer.ownerName,
    tone: customer.filingStatus === 'filed' ? 'success' : 'warning',
  })

  return events
}
